import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import PageBlock from "../../../components/PageBlock";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DashboardCard from "../../../components/DashboardCard";
import {
    DashboardStatisticInterface,
    DashboardStatisticResponse
} from "../../../types/Responses/DashboardStatisticResponse";
import {API} from "../../../types/API";
import {AxiosError, AxiosResponse} from "axios";
import DashboardCardSkeleton from "../../../components/Skeletons/DashboardCardSkeleton";
import {IconName} from "@fortawesome/free-regular-svg-icons";
import {
    DashboardDepositChartsResponseInterface,
    DashboardDepositObjectInterface, DashboardSalesResponseInterface
} from "../../../types/Responses/DashboardDepositChartsResponse";
import {
    DashboardWithdrawChartsResponseInterface,
    DashboardWithdrawObjectInterface
} from "../../../types/Responses/DashboardWithdrawChartsResponse";
import DashboardCharts from "../../../components/Charts/DashboardCharts";
import DatePickerMenu from "../../../components/DatePickerMenu";
import {
    DashboardLinearChartsResponseInterface,
    DashboardLinearObjectInterface,
    DashboardLinearAmountChartInterface,
    DashboardLinearCountChartInterface
} from "../../../types/Responses/DashboardLinearChartsResponse";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {useTranslation} from "react-i18next";
import {useUserContext} from "../../../types/contexts/UserContext";
import {setTitle} from "../../../hooks/setTitle";
import {Card, CardBody, Typography} from "@material-tailwind/react";
import BarChart from "../../../components/Charts/BarChart";
import LinearChart from "../../../components/Charts/LinearChart";
import LocationsChart from "../../../components/Charts/LocationsChart";
import Forbidden from "../../../components/Forbidden";
import {APILink} from "../../../types/APILink";
import {ChartPeriod} from "../../../types/ChartPeriod";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import DashboardAreaCharts from "../../../components/Charts/DashboardAreaCharts";
import TitleWithInfoTooltip from "../../../components/TitleWithInfoTooltip";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {setDepositLoading, setLinearLoading, setSalesLoading, setWithdrawLoading} from "../../../reducers/dashboard";
import {setSidebarTitle} from "../../../reducers/sidebar";

const cardTooltipTranslationKeys: Record<string, string> = {
    'dashboard.cards.today_sales': 'dashboard.tooltips.todays_sales',
    'dashboard.cards.yesterday_sales': 'dashboard.tooltips.yesterdays_sales',
    'dashboard.cards.yesterday_transactions': 'dashboard.tooltips.yesterdays_transactions',
};

const DATA_STORAGE_DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_LABEL_FORMAT = 'MMM YYYY';

const getMonthlyKey = (date: moment.Moment) => date.clone().startOf('month').format(DATA_STORAGE_DATE_FORMAT);

const aggregateAmountByMonth = (items: DashboardLinearAmountChartInterface[]): DashboardLinearAmountChartInterface[] => {
    const grouped: Record<string, DashboardLinearAmountChartInterface> = {};

    items.forEach((item) => {
        const itemDate = moment(item.date ?? item.title);
        if (!itemDate.isValid()) {
            return;
        }

        const monthKey = getMonthlyKey(itemDate);
        const existingMonth = grouped[monthKey];

        if (existingMonth) {
            existingMonth.amount += item.amount;
            return;
        }

        grouped[monthKey] = {
            amount: item.amount,
            date: monthKey,
            title: itemDate.clone().startOf('month').format(MONTH_LABEL_FORMAT)
        };
    });

    return Object.values(grouped)
        .sort((a, b) => moment(a.date, DATA_STORAGE_DATE_FORMAT).diff(moment(b.date, DATA_STORAGE_DATE_FORMAT)));
};

const aggregateCountByMonth = (items: DashboardLinearCountChartInterface[]): DashboardLinearCountChartInterface[] => {
    const grouped: Record<string, DashboardLinearCountChartInterface> = {};

    items.forEach((item) => {
        const itemDate = moment(item.date ?? item.title);
        if (!itemDate.isValid()) {
            return;
        }

        const monthKey = getMonthlyKey(itemDate);
        const existingMonth = grouped[monthKey];

        if (existingMonth) {
            existingMonth.count += item.count;
            return;
        }

        grouped[monthKey] = {
            count: item.count,
            date: monthKey,
            title: itemDate.clone().startOf('month').format(MONTH_LABEL_FORMAT)
        };
    });

    return Object.values(grouped)
        .sort((a, b) => moment(a.date, DATA_STORAGE_DATE_FORMAT).diff(moment(b.date, DATA_STORAGE_DATE_FORMAT)));
};

const shouldGroupLinearChartsByMonth = (start: string, end: string, period: ChartPeriod): boolean => {
    const startDate = moment(start, DATA_STORAGE_DATE_FORMAT);
    const endDate = moment(end, DATA_STORAGE_DATE_FORMAT);

    if (!startDate.isValid() || !endDate.isValid()) {
        return false;
    }

    const monthsCovered = endDate.clone().startOf('month').diff(startDate.clone().startOf('month'), 'months');
    if (monthsCovered < 1) {
        return false;
    }

    const isEarlyInYear = period === ChartPeriod.THIS_YEAR && moment().month() < 2;
    return !isEarlyInYear;
};

const transformLinearChartsData = (
    linearCharts: DashboardLinearObjectInterface,
    start: string,
    end: string,
    period: ChartPeriod
): DashboardLinearObjectInterface => {
    if (!shouldGroupLinearChartsByMonth(start, end, period)) {
        return linearCharts;
    }

    return {
        amount: aggregateAmountByMonth(linearCharts.amount),
        count: aggregateCountByMonth(linearCharts.count)
    };
};

const Dashboard = () => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const user = useUserContext();
    const handleError = useErrorHandler();
    const dispatch = useDispatch();
    const loading = useSelector((state: RootState) => state.dashboard);
    const dateFormat = 'YYYY-MM-DD';
    const [cards, setCards] = useState<DashboardStatisticInterface[]>();
    const [chartDateStart, setChartDateStart] = useState<string>(moment().format(dateFormat));
    const [chartDateEnd, setChartDateEnd] = useState<string>(moment().format(dateFormat));
    const [depositCharts, setDepositCharts] = useState<DashboardDepositObjectInterface>();
    const [withdrawCharts, setWithdrawCharts] = useState<DashboardWithdrawObjectInterface>();
    const [linearCharts, setLinearCharts] = useState<DashboardLinearObjectInterface>();
    const [salesCharts, setSalesCharts] = useState<DashboardSalesResponseInterface>();
    const [period, setPeriod] = useState<ChartPeriod>(ChartPeriod.TODAY);
    const [isLoading, setIsLoading] = useState<boolean>(false);


    const setStartDate = useCallback((v: string) => {
        setChartDateStart(v);
        if (moment(v).diff(moment(chartDateEnd)) > 0) {
            setChartDateEnd(v);
        }
    }, [setChartDateStart, setChartDateEnd, chartDateEnd]);


    const loadLinearCharts = useCallback((from: string, to: string, selectedPeriod?: ChartPeriod) => {
        setDepositCharts({successful: [], declined: [], outdated: []});
        dispatch(setLinearLoading(true));
        API.getLinearChart(from, to).then((response: AxiosResponse) => {
            const chartsResponse = response.data as DashboardLinearChartsResponseInterface;
            const transformedCharts = transformLinearChartsData(
                chartsResponse.linear,
                from,
                to,
                selectedPeriod ?? period
            );
            setLinearCharts(transformedCharts);
            dispatch(setLinearLoading(false));
        }).catch((e: AxiosError) => {
            dispatch(setLinearLoading(false));
            if (e?.response?.status === 403) {
                setLinearCharts(undefined);
            } else ErrorHandler(e, `get${APILink.GET_LINEAR_CHART}`, toast, handleError)
        });
    }, [setDepositCharts, setLinearCharts, toast, handleError, dispatch, setLinearLoading, period]);

    const loadDepositCharts = useCallback((from: string, to: string) => {
        setDepositCharts({successful: [], declined: [], outdated: []});
        dispatch(setDepositLoading(true));
        API.getDepositChart(from, to).then((response: AxiosResponse) => {
            const depositChartsResponse = response.data as DashboardDepositChartsResponseInterface;
            setDepositCharts(depositChartsResponse.payment_methods);
            dispatch(setDepositLoading(false));
        }).catch((e: AxiosError) => {
            dispatch(setDepositLoading(false));
            if (e?.response?.status === 403) {
                setDepositCharts(undefined);
            } else ErrorHandler(e, `get${APILink.GET_DEPOSIT_CHART}`, toast, handleError)
        });
    }, [setDepositCharts, handleError, toast, setDepositLoading, dispatch]);

    const loadWithdrawCharts = useCallback((from: string, to: string) => {
        setWithdrawCharts({successful: [], pending: [], declined: []});
        dispatch(setWithdrawLoading(true));
        API.getWithdrawChart(from, to).then((response: AxiosResponse) => {
            const withdrawChartsResponse = response.data as DashboardWithdrawChartsResponseInterface;
            setWithdrawCharts({...withdrawChartsResponse.providers, pending: []});
            dispatch(setWithdrawLoading(false));
        }).catch((e: AxiosError) => {
            dispatch(setWithdrawLoading(false));
            if (e?.response?.status === 403) {
                setWithdrawCharts(undefined);
            } else ErrorHandler(e, `get${APILink.GET_WITHDRAW_CHART}`, toast, handleError)
        });
    }, [setWithdrawCharts, toast, handleError, dispatch, setWithdrawLoading]);

    const loadSalesCharts = useCallback((from: string, to: string) => {
        setSalesCharts({decline_reasons: [], device_types: [], approved_ratio: [], locations: []});
        dispatch(setSalesLoading(true));
        API.getSalesChart(from, to).then((response: AxiosResponse) => {
            const salesChartsResponse = response.data.sales as DashboardSalesResponseInterface;
            setSalesCharts(salesChartsResponse);
            dispatch(setSalesLoading(false));
        }).catch((e: AxiosError) => {
            dispatch(setSalesLoading(false));
            if (e?.response?.status === 403) {
                setSalesCharts(undefined);
            } else ErrorHandler(e, `get${APILink.GET_SALES_CHART}`, toast, handleError)
        });
    }, [toast, handleError,dispatch, setSalesLoading]);

    const loadCharts = useCallback((from?: string, to?: string, currentPeriod?: ChartPeriod) => {
        if (!isLoading) {
            setIsLoading(true);
            const start = from ?? chartDateStart;
            const end = to ?? chartDateEnd;
            const periodToUse = currentPeriod ?? period;
            loadDepositCharts(start, end);
            loadWithdrawCharts(start, end);
            loadLinearCharts(start, end, periodToUse);
            loadSalesCharts(start, end);
        }
    }, [chartDateStart, chartDateEnd, isLoading, loadSalesCharts, loadDepositCharts, loadWithdrawCharts, loadLinearCharts, period]);

    const confirmDatePickerChange = useCallback((from: string, to: string, p: ChartPeriod) => {
        setIsLoading(false);
        setPeriod(p);
        loadCharts(from, to, p);
    }, [loadCharts]);

    useEffect(() => {
        setTitle(t('admin.menu.dashboard'));
        dispatch(setSidebarTitle(t('admin.menu.dashboard')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                API.dashboardStatistic().then((response: AxiosResponse) => {
                    const cardsResponse = response.data.data as DashboardStatisticResponse;
                    setCards(cardsResponse.cards);
                }).catch(e => ErrorHandler(e, `get${APILink.DASHBOARD_STATISTIC}`, toast, handleError));
                loadCharts(chartDateStart, chartDateEnd, period);
            }).catch();
        }
    }, [chartDateEnd, chartDateStart, t, handleError, user, loadCharts, toast, period]);

    const cardsSkeletonsUI = useMemo(() => {
        return <>
            <DashboardCardSkeleton/>
            <DashboardCardSkeleton/>
            <DashboardCardSkeleton/>
            <DashboardCardSkeleton/>
        </>;
    }, []);

    const cardsUI = useMemo(() => {
        if (!isLoading)
            return <></>;
        if (!user || !user.user || !cards)
            return cardsSkeletonsUI;
        return cards.map((card) => {
            const tooltipKey = cardTooltipTranslationKeys[card.name];
            return <DashboardCard key={card.name} color={card.color} name={t(card.name)}
                                  count={card.count + (card.name.toLowerCase().split('deposits').length > 1 ? ' ' + user.user.currency : '')}
                                  tooltip={tooltipKey ? t(tooltipKey) : undefined}
                                  icon={<FontAwesomeIcon icon={card.icon as IconName}
                                                         className="relative w-5 h-5 md:w-6 md:h-6"/>}/>
        })
    }, [cards, cardsSkeletonsUI, user, t, isLoading]);

    const datePickerMenuUI = useMemo(() => {
        const isNotForbidden = linearCharts || depositCharts || withdrawCharts || salesCharts || (cards && cards.length > 0);
        if (!isNotForbidden || !isLoading)
            return <></>;
        console.log(period)
        return <DatePickerMenu dateFormat={dateFormat} startDate={chartDateStart} endDate={chartDateEnd}
                               setEnd={setChartDateEnd} setStart={setStartDate} onChanged={confirmDatePickerChange}
                               defaultPeriod={period}/>
    }, [chartDateEnd, chartDateStart, isLoading, confirmDatePickerChange, setStartDate, linearCharts, depositCharts, withdrawCharts, salesCharts, cards, period]);


    const salesChartsUI = useMemo(() => {
        if (!salesCharts)
            return <></>

        return <div className="w-full">
            <div className="grid md:grid-cols-2 gap-4 mt-12 mb-4">
                <Card key={t('admin.charts.decline_reasons')}
                      className="bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="w-full px-2 block h-96" id='device_types'>
                        <Typography variant="h6" className="mb-6 text-center">
                            <TitleWithInfoTooltip
                                title={t('admin.charts.decline_reasons')}
                                tooltip={t('dashboard.tooltips.decline_reasons')}
                                className="justify-center"
                            />
                        </Typography>
                        <BarChart
                            isLoading={loading.sales}
                            series={{
                                name: t('Count'),
                                data: salesCharts.decline_reasons.map(item => item.count) ?? []
                            }}
                            labels={salesCharts.decline_reasons.map(item => t(item.name)) ?? []}
                        />
                    </CardBody>
                </Card>
                <Card key={t('admin.charts.approved_ratio')}
                      className="bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="w-full px-2 block h-96" id='device_types'>
                        <Typography variant="h6" className="mb-6 text-center">
                            <TitleWithInfoTooltip
                                title={t('admin.charts.approved_ratio')}
                                tooltip={t('dashboard.tooltips.approved_ratio')}
                                className="justify-center"
                            />
                        </Typography>
                        <LinearChart
                            isLoading={loading.sales}
                            series={[{name: t('Percents'), data: salesCharts.approved_ratio.map(item => item.count)}]}
                            labels={salesCharts.approved_ratio.map(item => item.title)}/>
                    </CardBody>
                </Card>
            </div>
            <div className="w-full mt-12 mb-4">
                <Card key={t('admin.charts.locations')} className="bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="w-full px-2 block min-h-[24rem] h-auto lg:h-96" id='locations_chart'>
                        <Typography variant="h6" className="mb-6 text-center">
                            <TitleWithInfoTooltip
                                title={t('admin.charts.locations')}
                                tooltip={t('dashboard.tooltips.locations')}
                                className="justify-center"
                            />
                        </Typography>
                        <LocationsChart
                            isLoading={loading.sales}
                            series={[
                            ['Country', t('admin.charts.locations_table.transactions'), t('admin.charts.locations_table.percent_with_prefix')],
                            ...salesCharts.locations.map(item => [item.name, item.count, parseFloat(item.percent)])
                        ]} extra={salesCharts.locations.map(item => [item.percent])}/>
                    </CardBody>
                </Card>
            </div>
        </div>
    }, [salesCharts, t, loading]);

    const depositAndWithdrawChartsUI = useMemo(() => {
        if (!depositCharts || !withdrawCharts || !salesCharts)
            return <></>;

        return <DashboardCharts elements={[
            {
                title: t('admin.dashboard.deposits.successful'),
                tooltip: t('dashboard.tooltips.successful_deposits'),
                data: {
                    labels: depositCharts.successful.map(item => item.name),
                    series: depositCharts.successful.map(item => item.count),
                    isLoading: loading.deposit
                }
            },
            {
                title: t('admin.dashboard.deposits.declined'),
                tooltip: t('dashboard.tooltips.declined_deposits'),
                data: {
                    labels: depositCharts.declined.map(item => item.name),
                    series: depositCharts.declined.map(item => item.count),
                    isLoading: loading.deposit
                }
            },
            {
                title: t('admin.dashboard.deposits.outdated'),
                tooltip: t('dashboard.tooltips.outdated_deposits'),
                data: {
                    labels: depositCharts.outdated.map(item => item.name),
                    series: depositCharts.outdated.map(item => item.count),
                    isLoading: loading.deposit
                }
            },
            {
                title: t('admin.dashboard.withdraws.successful'),
                tooltip: t('dashboard.tooltips.successful_withdrawals'),
                data: {
                    labels: withdrawCharts.successful.map(item => item.name),
                    series: withdrawCharts.successful.map(item => item.count),
                    isLoading: loading.withdraws
                }
            },
            {
                title: t('admin.dashboard.withdraws.declined'),
                tooltip: t('dashboard.tooltips.declined_withdrawals'),
                data: {
                    labels: withdrawCharts.declined.map(item => item.name),
                    series: withdrawCharts.declined.map(item => item.count),
                    isLoading: loading.withdraws
                }
            },
            {
                title: t('admin.charts.device_types'),
                tooltip: t('dashboard.tooltips.device_types'),
                data: {
                    labels: salesCharts.device_types.map(item => t(item.name)),
                    series: salesCharts.device_types.map(item => item.count),
                    isLoading: loading.sales
                },
            }
        ]}/>
    }, [depositCharts, withdrawCharts, salesCharts, t, loading]);

    const linearChartsUI = useMemo(() => {
        if (!linearCharts)
            return <></>;

        return <DashboardAreaCharts
            elements={[
            {
                title: t('admin.dashboard.deposits.amount'),
                tooltip: t('dashboard.tooltips.successful_deposits_amount'),
                data: {
                    labels: linearCharts.amount.map(item => item.title),
                    series: [{name: t('Amount'), data: linearCharts.amount.map(item => item.amount)}],
                    isLoading: loading.linear
                }
            },
            {
                title: t('admin.dashboard.deposits.count'),
                tooltip: t('dashboard.tooltips.successful_deposits_count'),
                data: {
                    labels: linearCharts.count.map(item => item.title),
                    series: [{name: t('Count'), data: linearCharts.count.map(item => item.count)}],
                    isLoading: loading.linear
                }
            }
        ]}/>
    }, [linearCharts, t, loading]);

    const emptyDashboard = useMemo(() => {
        if (linearCharts || depositCharts || withdrawCharts || salesCharts || (cards && cards.length > 0) || !isLoading)
            return <></>

        return <Forbidden isForbidden={true}/>
    }, [linearCharts, depositCharts, withdrawCharts, salesCharts, cards, isLoading]);

    return (
        <PageBlock>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mt-4">
                {cardsUI}
            </div>
            {datePickerMenuUI}
            {depositAndWithdrawChartsUI}
            {linearChartsUI}
            {salesChartsUI}
            {emptyDashboard}
        </PageBlock>
    );
}
export default memo(Dashboard);
